/**
 * Contingency Panel — Inject contingencies during simulation
 */

import { useState } from 'react'
import type { AARTanker, AARReceiver, AARContingency } from '../../../../types'

interface ContingencyPanelProps {
  tankers: AARTanker[]
  receivers: AARReceiver[]
  contingencies: AARContingency[]
  onInjectContingency: (contingency: AARContingency) => void
  disabled: boolean
  missionAborted: boolean
}

let contingencyCounter = 0

export function ContingencyPanel({ tankers, receivers, contingencies, onInjectContingency, disabled, missionAborted }: ContingencyPanelProps) {
  const [expanded, setExpanded] = useState(false)
  const [rtbMinutesInput, setRtbMinutesInput] = useState<Record<string, string>>({})

  const activeTankers = tankers.filter(t => t.status !== 'CANCELLED' && t.status !== 'RTB')
  const activeReceivers = receivers.filter(r => r.status !== 'COMPLETE' && r.status !== 'DIVERTED')
  const reducibleReceivers = activeReceivers.filter(r => r.flightSize > 1)

  const injectTankerCancel = (tankerId: string) => {
    const tnk = tankers.find(t => t.id === tankerId)
    if (!tnk) return
    onInjectContingency({
      id: `CTG-${++contingencyCounter}`,
      type: 'TANKER_CANCEL',
      description: `${tnk.callsign} operations cancelled — receivers reflow`,
      triggerTime: 0,
      affectedId: tankerId,
      applied: false,
    })
  }

  const injectTankerRtbEarly = (tankerId: string) => {
    const tnk = tankers.find(t => t.id === tankerId)
    if (!tnk) return
    const minutes = parseInt(rtbMinutesInput[tankerId] ?? '0') || 0
    onInjectContingency({
      id: `CTG-${++contingencyCounter}`,
      type: 'TANKER_RTB_EARLY',
      description: `${tnk.callsign} RTB in ${minutes} min${minutes === 0 ? ' (after current receiver)' : ''}`,
      triggerTime: 0,
      affectedId: tankerId,
      applied: false,
      rtbMinutes: minutes,
    })
  }

  const injectReceiverEmergency = (receiverId: string) => {
    const rcv = receivers.find(r => r.id === receiverId)
    if (!rcv) return
    onInjectContingency({
      id: `CTG-${++contingencyCounter}`,
      type: 'RECEIVER_EMERGENCY',
      description: `${rcv.callsign} declares EMERGENCY — priority refueling`,
      triggerTime: 0,
      affectedId: receiverId,
      applied: false,
    })
  }

  const injectFlightReduce = (receiverId: string) => {
    const rcv = receivers.find(r => r.id === receiverId)
    if (!rcv) return
    onInjectContingency({
      id: `CTG-${++contingencyCounter}`,
      type: 'FLIGHT_REDUCE',
      description: `${rcv.callsign} flight reduced by 1 (${rcv.flightSize} → ${rcv.flightSize - 1})`,
      triggerTime: 0,
      affectedId: receiverId,
      applied: false,
      reduceBy: 1,
    })
  }

  const injectFuelShortfall = (tankerId: string) => {
    const tnk = tankers.find(t => t.id === tankerId)
    if (!tnk) return
    onInjectContingency({
      id: `CTG-${++contingencyCounter}`,
      type: 'FUEL_SHORTFALL',
      description: `${tnk.callsign} fuel shortfall — 30% reduction in available offload`,
      triggerTime: 0,
      affectedId: tankerId,
      applied: false,
    })
  }

  const injectWeatherDivert = (receiverId: string) => {
    const rcv = receivers.find(r => r.id === receiverId)
    if (!rcv) return
    onInjectContingency({
      id: `CTG-${++contingencyCounter}`,
      type: 'WEATHER_DIVERT',
      description: `${rcv.callsign} weather divert — departing AR track`,
      triggerTime: 0,
      affectedId: receiverId,
      applied: false,
    })
  }

  return (
    <div className="term-box p-3">
      {/* Mission Abort Banner */}
      {missionAborted && (
        <div className="bg-red-500/20 border border-red-500/50 rounded px-3 py-2 mb-3 flex items-center gap-2">
          <span className="text-red-400 font-bold text-xs uppercase tracking-wider animate-pulse">MISSION ABORT</span>
          <span className="text-[10px] text-red-300">Insufficient tanker capacity for mission-essential receivers</span>
        </div>
      )}

      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
          Contingency Inject
        </div>
        <span className="text-[10px] text-term-textDim">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Tanker Cancel */}
          {activeTankers.length > 0 && (
            <div>
              <div className="text-[10px] text-term-error font-bold uppercase mb-1">Cancel Tanker</div>
              <div className="flex flex-wrap gap-1">
                {activeTankers.map(tnk => (
                  <button
                    key={tnk.id}
                    onClick={() => injectTankerCancel(tnk.id)}
                    disabled={disabled}
                    className="px-2 py-1 text-[10px] font-mono bg-red-500/10 border border-red-500/30 text-red-400 rounded hover:bg-red-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {tnk.callsign}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tanker RTB Early */}
          {activeTankers.length > 0 && (
            <div>
              <div className="text-[10px] text-orange-400 font-bold uppercase mb-1">Tanker RTB Early</div>
              <div className="flex flex-wrap gap-2">
                {activeTankers.map(tnk => (
                  <div key={tnk.id} className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={999}
                      value={rtbMinutesInput[tnk.id] ?? '0'}
                      onChange={e => setRtbMinutesInput(prev => ({ ...prev, [tnk.id]: e.target.value }))}
                      className="term-input py-0.5 px-1 w-12 text-[10px] tabular-nums text-center"
                      title="Minutes until RTB (0 = after current)"
                    />
                    <button
                      onClick={() => injectTankerRtbEarly(tnk.id)}
                      disabled={disabled}
                      className="px-2 py-1 text-[10px] font-mono bg-orange-500/10 border border-orange-500/30 text-orange-400 rounded hover:bg-orange-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {tnk.callsign} RTB
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flight Reduce */}
          {reducibleReceivers.length > 0 && (
            <div>
              <div className="text-[10px] text-purple-400 font-bold uppercase mb-1">Reduce Flight (-1 ship)</div>
              <div className="flex flex-wrap gap-1">
                {reducibleReceivers.map(rcv => (
                  <button
                    key={rcv.id}
                    onClick={() => injectFlightReduce(rcv.id)}
                    disabled={disabled}
                    className="px-2 py-1 text-[10px] font-mono bg-purple-500/10 border border-purple-500/30 text-purple-400 rounded hover:bg-purple-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {rcv.callsign} (×{rcv.flightSize})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Receiver Emergency */}
          {activeReceivers.length > 0 && (
            <div>
              <div className="text-[10px] text-amber-400 font-bold uppercase mb-1">Receiver Emergency</div>
              <div className="flex flex-wrap gap-1">
                {activeReceivers.filter(r => r.status !== 'EMERGENCY').map(rcv => (
                  <button
                    key={rcv.id}
                    onClick={() => injectReceiverEmergency(rcv.id)}
                    disabled={disabled}
                    className="px-2 py-1 text-[10px] font-mono bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded hover:bg-amber-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {rcv.callsign}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fuel Shortfall */}
          {activeTankers.length > 0 && (
            <div>
              <div className="text-[10px] text-term-warning font-bold uppercase mb-1">Fuel Shortfall (-30%)</div>
              <div className="flex flex-wrap gap-1">
                {activeTankers.map(tnk => (
                  <button
                    key={tnk.id}
                    onClick={() => injectFuelShortfall(tnk.id)}
                    disabled={disabled}
                    className="px-2 py-1 text-[10px] font-mono bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 rounded hover:bg-yellow-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {tnk.callsign}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Weather Divert */}
          {activeReceivers.length > 0 && (
            <div>
              <div className="text-[10px] text-cyan-400 font-bold uppercase mb-1">Weather Divert</div>
              <div className="flex flex-wrap gap-1">
                {activeReceivers.map(rcv => (
                  <button
                    key={rcv.id}
                    onClick={() => injectWeatherDivert(rcv.id)}
                    disabled={disabled}
                    className="px-2 py-1 text-[10px] font-mono bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded hover:bg-cyan-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {rcv.callsign}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Contingency History */}
          {contingencies.length > 0 && (
            <div className="border-t border-term-border/30 pt-2">
              <div className="text-[10px] text-term-textDim font-bold uppercase mb-1">Applied</div>
              {contingencies.filter(c => c.applied).map(c => (
                <div key={c.id} className="text-[10px] text-term-textDim line-through">
                  {c.description}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
